import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatPLN, apiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BudgetCard from "@/components/BudgetCard";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Trash2, Send, AlertTriangle, Sparkles, Info } from "lucide-react";

const MIN_ORDER_NET = 300;

export default function CurrentOrder() {
  const { lines, setQty, remove, total, clear } = useCart();
  const [budget, setBudget] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => {
      if (r.data.site) {
        setBudget(r.data);
      }
    });
  }, []);

  const submit = async () => {
    if (lines.length === 0) {
      toast.error("Koszyk jest pusty");
      return;
    }
    if (total < MIN_ORDER_NET) {
      toast.error(`Minimalna wartość zamówienia to ${MIN_ORDER_NET} PLN netto. Twoje zamówienie: ${total.toFixed(2)} PLN.`);
      return;
    }
    setSubmitting(true);
    try {
      const items = lines.map((l) => {
        if (l.product.custom_request_id) {
          return { custom_request_id: l.product.custom_request_id, quantity: Number(l.quantity) };
        }
        return { product_id: l.product.id, quantity: Number(l.quantity) };
      });
      const { data } = await api.post("/orders", { items, employee_comment: comment, submit: true });
      toast.success(
        data.status === "zaakceptowane"
          ? "Zamówienie automatycznie zaakceptowane"
          : "Zamówienie wysłane do akceptacji kierownika",
      );
      clear();
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const overBudget = budget && total > budget.remaining;
  const belowMin = lines.length > 0 && total < MIN_ORDER_NET;
  const missingToMin = Math.max(0, MIN_ORDER_NET - total);

  return (
    <div>
      <PageHeader
        testId="current-order-page"
        title="Aktualne zamówienie"
        subtitle={lines.length === 0 ? "Koszyk jest pusty" : `${lines.length} pozycji · wartość ${formatPLN(total)}`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="ol-card overflow-hidden hidden md:block">
            <table className="ol-table" data-testid="cart-table">
              <thead>
                <tr>
                  <th>Produkt</th>
                  <th>Jedn.</th>
                  <th className="text-right">Cena</th>
                  <th className="text-center w-32">Ilość</th>
                  <th className="text-right">Wartość</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.product.id}>
                    <td>
                      <p className="font-medium text-slate-900">{l.product.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{l.product.sku}</p>
                    </td>
                    <td className="text-slate-600">{l.product.unit}</td>
                    <td className="text-right">{formatPLN(l.product.price_net)}</td>
                    <td className="text-center">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={l.quantity}
                        onChange={(e) => setQty(l.product.id, Math.max(0, parseInt(e.target.value) || 0))}
                        data-testid={`qty-${l.product.sku}`}
                        className="w-20 text-center px-2 py-1.5 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
                      />
                    </td>
                    <td className="text-right font-semibold">{formatPLN(l.product.price_net * l.quantity)}</td>
                    <td className="text-right">
                      <button onClick={() => remove(l.product.id)} data-testid={`remove-${l.product.sku}`} className="text-slate-400 hover:text-red-600 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500">
                    Brak pozycji. <Link to="/catalog" className="ol-link">Przejdź do katalogu</Link>.
                  </td></tr>
                )}
              </tbody>
              {lines.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4} className="text-right font-semibold text-slate-700">Suma netto:</td>
                    <td className="text-right text-lg font-bold text-slate-900" data-testid="cart-total">{formatPLN(total)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Mobile cart list */}
          <div className="md:hidden space-y-3" data-testid="cart-mobile-list">
            {lines.length === 0 ? (
              <div className="ol-card p-8 text-center text-sm text-slate-500">
                Brak pozycji. <Link to="/catalog" className="ol-link">Przejdź do katalogu</Link>.
              </div>
            ) : (
              <>
                {lines.map((l) => (
                  <div key={l.product.id} className="ol-card p-4" data-testid={`cart-card-${l.product.sku}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[10px] text-slate-400 uppercase">{l.product.sku}</p>
                        <p className="font-semibold text-slate-900 text-sm leading-tight">{l.product.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{formatPLN(l.product.price_net)} / {l.product.unit}</p>
                      </div>
                      <button onClick={() => remove(l.product.id)} data-testid={`remove-mobile-${l.product.sku}`} className="text-slate-400 hover:text-red-600 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setQty(l.product.id, Math.max(1, l.quantity - 1))}
                          className="w-9 h-9 rounded-md border border-slate-300 hover:border-[#FF6B1A] text-slate-700 font-semibold"
                          data-testid={`dec-${l.product.sku}`}
                        >−</button>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={l.quantity}
                          onChange={(e) => setQty(l.product.id, Math.max(0, parseInt(e.target.value) || 0))}
                          data-testid={`qty-mobile-${l.product.sku}`}
                          className="w-14 text-center px-1 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:outline-none text-sm"
                        />
                        <button
                          onClick={() => setQty(l.product.id, l.quantity + 1)}
                          className="w-9 h-9 rounded-md border border-slate-300 hover:border-[#FF6B1A] text-slate-700 font-semibold"
                          data-testid={`inc-${l.product.sku}`}
                        >+</button>
                      </div>
                      <p className="text-base font-bold text-slate-900">{formatPLN(l.product.price_net * l.quantity)}</p>
                    </div>
                  </div>
                ))}
                <div className="ol-card p-4 flex items-center justify-between bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">Suma netto</span>
                  <span className="text-lg font-bold text-slate-900" data-testid="cart-total-mobile">{formatPLN(total)}</span>
                </div>
              </>
            )}
          </div>

          <div className="ol-card p-5">
            <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Komentarz do zamówienia (opcjonalnie)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="order-comment"
              rows={3}
              placeholder="Np. priorytetowo dostarczyć BHP…"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>

          {overBudget && (
            <div className="ol-card p-4 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="over-budget-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-900">Uwaga: zamówienie przekracza dostępny budżet.</p>
                <p className="text-amber-800 mt-1">Możesz je wysłać, ale trafi do kierownika z ostrzeżeniem. Możesz też <Link to="/budget-request" className="underline font-semibold">poprosić o zwiększenie budżetu</Link>.</p>
              </div>
            </div>
          )}

          {belowMin && (
            <div className="ol-card p-4 border-red-300 bg-red-50 flex items-start gap-3" data-testid="below-min-warning">
              <Info className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-900">Minimalna wartość zamówienia to {formatPLN(MIN_ORDER_NET)} netto.</p>
                <p className="text-red-800 mt-1">
                  Brakuje <strong>{formatPLN(missingToMin)}</strong>, aby móc wysłać zamówienie.{" "}
                  <Link to="/suggestions" className="underline font-semibold inline-flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Zobacz sugestie
                  </Link>.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {budget && (
            <BudgetCard
              monthly={budget.monthly_budget}
              used={budget.used_budget_current_month}
              remaining={budget.remaining}
              pendingValue={total}
            />
          )}

          <button
            onClick={submit}
            disabled={submitting || lines.length === 0 || belowMin}
            data-testid="submit-order-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
          >
            <Send className="w-4 h-4" /> {submitting ? "Wysyłanie…" : belowMin ? `Min. ${formatPLN(MIN_ORDER_NET)}` : "Wyślij zamówienie"}
          </button>

          {budget && (
            <div className="text-xs text-slate-500 text-center">
              {budget.site.approval_required_for_all_orders
                ? "Zamówienie zostanie wysłane do akceptacji kierownika."
                : `Zamówienia do ${formatPLN(budget.site.auto_approve_limit)} mogą być automatycznie zaakceptowane.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
